import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Mandat,
    getMandatById,
    uploadMandatPdf,
    getMandatPdfUrl,
    updateMandat,
} from '@app/api/data/mandats';
import {
    Card,
    Typography,
    Divider,
    Button,
    Box,
    Alert,
    Sheet,
    Stack,
    Grid,
    Link,
    Textarea,
    Chip,
} from '@mui/joy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EditIcon from '@mui/icons-material/Edit';
import { AppIcon } from '@app/components/AppIcon';
import AppLoading from '@app/components/AppLoading';
import {
    uploadPotentielsCsvFile,
    getPotentielsCsvFilesByMandat,
    deletePotentielsCsvFile,
} from '@app/api/data/potentiels-csv-file';

interface CsvFile {
    id: number;
    filename: string;
    createdAt: string;
    collaborateurId: number | null;
    collaborateur: {
        id: number;
        userName: string;
    } | null;
}

const MandatDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [mandat, setMandat] = useState<Mandat | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [fileUploading, setFileUploading] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [pdfFilename, setPdfFilename] = useState<string | null>(null);
    const [csvUploading, setCsvUploading] = useState(false);
    const [csvError, setCsvError] = useState<string | null>(null);
    const [csvSuccess, setCsvSuccess] = useState<string | null>(null);
    const [csvFiles, setCsvFiles] = useState<CsvFile[]>([]);
    const [csvListLoading, setCsvListLoading] = useState(false);
    const [csvListError, setCsvListError] = useState<string | null>(null);
    const [isEditingPublicites, setIsEditingPublicites] = useState(false);
    const [editedPublicites, setEditedPublicites] = useState<string>('');
    const [publicitesLoading, setPublicitesLoading] = useState(false);
    const [publicitesError, setPublicitesError] = useState<string | null>(null);
    const [publicitesSuccess, setPublicitesSuccess] = useState<string | null>(
        null
    );

    const fetchMandat = useCallback(async () => {
        if (!id) {
            setError('Identifiant manquant');
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await getMandatById(id);
            if (response?.data) {
                setMandat(response.data);

                // Vérifier s'il y a un PDF associé
                if (response.data.pdfPath) {
                    fetchPdfUrl();
                }
            } else {
                throw new Error('Aucune donnée reçue');
            }
        } catch (err: any) {
            console.error('Erreur lors du chargement:', err);
            setError(
                err?.message || 'Erreur lors du chargement des informations'
            );
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchPdfUrl = async () => {
        if (!id) return;

        try {
            const response = await getMandatPdfUrl(parseInt(id));
            if (response?.data?.url) {
                setPdfUrl(response.data.url);

                // Use filename from API response if available, otherwise extract from path
                if (response.data.filename) {
                    setPdfFilename(response.data.filename);
                } else if (mandat?.pdfPath) {
                    const pathParts = mandat.pdfPath.split('/');
                    const filename = pathParts[pathParts.length - 1];
                    setPdfFilename(filename);
                }
            }
        } catch (error) {
            console.error(
                "Erreur lors de la récupération de l'URL du PDF:",
                error
            );
        }
    };

    const handleFileUpload = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        if (!file || !id) return;

        // Vérifier le type de fichier
        if (file.type !== 'application/pdf') {
            setFileError('Seuls les fichiers PDF sont autorisés');
            return;
        }

        setFileUploading(true);
        setFileError(null);

        try {
            await uploadMandatPdf(parseInt(id), file);
            await fetchMandat(); // Recharger les données du mandat
        } catch (error: any) {
            console.error("Erreur lors de l'upload du PDF:", error);
            setFileError(
                error?.message || "Erreur lors de l'upload du fichier"
            );
        } finally {
            setFileUploading(false);
        }
    };

    const handleCsvUpload = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = event.target.files;
        if (!files || !id) return;

        setCsvUploading(true);
        setCsvError(null);
        setCsvSuccess(null);

        let errorCount = 0;
        let successCount = 0;

        const uploads = Array.from(files).map((file) => {
            return new Promise<void>((resolve) => {
                if (!file.name.endsWith('.csv')) {
                    setCsvError(
                        (prev) =>
                            (prev ? prev + '\n' : '') +
                            `${file.name} : format non supporté`
                    );
                    errorCount++;
                    resolve();
                    return;
                }
                const reader = new FileReader();
                reader.onload = async (e) => {
                    try {
                        const base64 = (e.target?.result as string)?.split(
                            ','
                        )[1];
                        if (!base64) {
                            setCsvError(
                                (prev) =>
                                    (prev ? prev + '\n' : '') +
                                    `${file.name} : erreur de lecture`
                            );
                            errorCount++;
                            resolve();
                            return;
                        }
                        await uploadPotentielsCsvFile({
                            filename: file.name,
                            fileBase64: base64,
                            mandatId: parseInt(id),
                        });
                        successCount++;
                    } catch (error: any) {
                        setCsvError(
                            (prev) =>
                                (prev ? prev + '\n' : '') +
                                `${file.name} : ${
                                    error?.response?.data?.message ||
                                    error?.message ||
                                    "Erreur lors de l'upload"
                                }`
                        );
                        errorCount++;
                    } finally {
                        resolve();
                    }
                };
                reader.onerror = () => {
                    setCsvError(
                        (prev) =>
                            (prev ? prev + '\n' : '') +
                            `${file.name} : erreur de lecture`
                    );
                    errorCount++;
                    resolve();
                };
                reader.readAsDataURL(file);
            });
        });

        await Promise.all(uploads);

        if (successCount > 0)
            setCsvSuccess(`${successCount} fichier(s) uploadé(s) avec succès`);
        setCsvUploading(false);
        fetchCsvFiles();
    };

    const fetchCsvFiles = useCallback(async () => {
        if (!id) return;
        setCsvListLoading(true);
        setCsvListError(null);
        try {
            const res = await getPotentielsCsvFilesByMandat(Number(id));
            setCsvFiles(res.data);
        } catch (e: any) {
            setCsvListError(
                e?.response?.data?.message ||
                    e?.message ||
                    'Erreur lors du chargement des fichiers CSV'
            );
        } finally {
            setCsvListLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchMandat();
        fetchCsvFiles();
    }, [fetchMandat, fetchCsvFiles]);

    // Rafraîchir la liste après upload réussi
    useEffect(() => {
        if (csvSuccess) fetchCsvFiles();
    }, [csvSuccess, fetchCsvFiles]);

    const handleDeleteCsv = async (csvId: number) => {
        try {
            await deletePotentielsCsvFile(csvId);
            fetchCsvFiles();
        } catch (e: any) {
            setCsvListError(
                e?.response?.data?.message ||
                    e?.message ||
                    'Erreur lors de la suppression du fichier CSV'
            );
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleStartEditPublicites = () => {
        // Convert semicolons to newlines for easier editing
        const listWithNewlines = mandat?.listePublicites
            ? mandat.listePublicites
                  .split(';')
                  .map((item) => item.trim())
                  .join('\n')
            : '';
        setEditedPublicites(listWithNewlines);
        setPublicitesError(null);
        setPublicitesSuccess(null);
        setIsEditingPublicites(true);
    };

    const handleCancelEditPublicites = () => {
        setIsEditingPublicites(false);
        setEditedPublicites('');
        setPublicitesError(null);
        setPublicitesSuccess(null);
    };

    const handleSavePublicites = async () => {
        if (!id) return;

        setPublicitesLoading(true);
        setPublicitesError(null);
        setPublicitesSuccess(null);

        try {
            // Convert newlines back to semicolons
            const listWithSemicolons = editedPublicites
                .split('\n')
                .map((item) => item.trim())
                .filter((item) => item.length > 0)
                .join(';');

            await updateMandat(parseInt(id), {
                listePublicites: listWithSemicolons,
            });
            setPublicitesSuccess('Liste mise à jour avec succès');
            await fetchMandat(); // Reload mandat data
            setTimeout(() => {
                setIsEditingPublicites(false);
                setPublicitesSuccess(null);
            }, 1500);
        } catch (error: any) {
            console.error('Erreur lors de la mise à jour:', error);
            setPublicitesError(
                error?.response?.data?.message ||
                    error?.message ||
                    'Erreur lors de la mise à jour'
            );
        } finally {
            setPublicitesLoading(false);
        }
    };

    if (loading) {
        return (
            <Sheet className="h-40vh flex items-center justify-center">
                <AppLoading />
            </Sheet>
        );
    }

    if (error) {
        return (
            <Box className="p-3">
                <Alert color="danger" className="mb-2">
                    {error}
                </Alert>
                <Button onClick={fetchMandat} className="mr-1">
                    Réessayer
                </Button>
                <Button
                    size="sm"
                    variant="soft"
                    color="primary"
                    onClick={handleBack}
                    startDecorator={<AppIcon icon="ArrowLeftIcon" />}
                >
                    Retour à la liste
                </Button>
            </Box>
        );
    }

    if (!mandat) {
        return (
            <Box className="p-3">
                <Alert color="warning">
                    Aucun mandat trouvé avec cet identifiant
                </Alert>
                <Button
                    size="sm"
                    variant="soft"
                    color="primary"
                    onClick={handleBack}
                    startDecorator={<AppIcon icon="ArrowLeftIcon" />}
                    className="mt-2"
                >
                    Retour à la liste
                </Button>
            </Box>
        );
    }

    return (
        <Box className="p-2">
            <Card variant="outlined" className="mb-3">
                <Box className="mb-2 flex items-center justify-between">
                    <Typography level="h3">Mandat: {mandat.ref}</Typography>
                    <Button
                        size="sm"
                        variant="soft"
                        color="primary"
                        onClick={handleBack}
                        startDecorator={<AppIcon icon="ArrowLeftIcon" />}
                    >
                        Retour à la liste
                    </Button>
                </Box>

                <Divider />

                <Grid container spacing={2} className="mt-2">
                    <Grid xs={12} md={6} className="space-y-4">
                        <Card variant="soft">
                            <Typography level="title-md" className="mb-1">
                                Informations générales
                            </Typography>
                            <Divider className="my-1" />

                            <Stack spacing={1} className="mt-2">
                                <Box>
                                    <Typography level="body-xs" color="neutral">
                                        Club:
                                    </Typography>
                                    <Typography>
                                        {mandat.ref || 'Non renseigné'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography level="body-xs" color="neutral">
                                        Budget:
                                    </Typography>
                                    <Typography>
                                        {mandat.budget
                                            ? `${mandat.budget} ${mandat.devise}`
                                            : 'Non renseigné'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography level="body-xs" color="neutral">
                                        Date d'accord:
                                    </Typography>
                                    <Typography>
                                        {new Date(
                                            mandat.dateAccord
                                        ).toLocaleDateString('fr-FR')}
                                    </Typography>
                                </Box>

                                {mandat.notes && (
                                    <Box className="mt-2">
                                        <Typography
                                            level="body-xs"
                                            color="neutral"
                                        >
                                            Notes:
                                        </Typography>
                                        <Typography>{mandat.notes}</Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Card>
                        <Card variant="soft">
                            <Typography level="title-md" className="mb-1">
                                Document du mandat
                            </Typography>
                            <Divider className="my-1" />

                            <Box className="mt-2">
                                <input
                                    accept="application/pdf"
                                    style={{ display: 'none' }}
                                    id="pdf-upload-input"
                                    type="file"
                                    onChange={handleFileUpload}
                                    disabled={fileUploading}
                                />
                                <label htmlFor="pdf-upload-input">
                                    <Button
                                        component="span"
                                        startDecorator={<CloudUploadIcon />}
                                        loading={fileUploading}
                                        disabled={fileUploading}
                                        className="mb-2"
                                    >
                                        {mandat.pdfPath
                                            ? 'Remplacer le PDF'
                                            : 'Uploader un PDF'}
                                    </Button>
                                </label>

                                {fileError && (
                                    <Alert color="danger" className="mt-2">
                                        {fileError}
                                    </Alert>
                                )}

                                {mandat.pdfPath && pdfUrl && (
                                    <Box className="mt-2">
                                        <Box className="flex items-center">
                                            <InsertDriveFileIcon
                                                sx={{ mr: 1 }}
                                            />
                                            <Link
                                                href={pdfUrl}
                                                target="_blank"
                                                rel="noopener"
                                            >
                                                Voir le document PDF
                                            </Link>
                                        </Box>
                                        {pdfFilename && (
                                            <Box className="ml-6 mt-1">
                                                <Typography
                                                    level="body-sm"
                                                    color="neutral"
                                                >
                                                    {pdfFilename}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {mandat.pdfPath && !pdfUrl && (
                                    <Box className="mt-2">
                                        <Box className="flex items-center">
                                            <InsertDriveFileIcon
                                                sx={{ mr: 1 }}
                                            />
                                            <Typography color="neutral">
                                                Document PDF disponible
                                            </Typography>
                                        </Box>
                                        {pdfFilename && (
                                            <Box className="ml-6 mt-1">
                                                <Typography
                                                    level="body-sm"
                                                    color="neutral"
                                                >
                                                    {pdfFilename}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Card>
                        <Card variant="soft">
                            <Typography level="title-md" className="mb-1">
                                Fichiers CSV Parrainage Potentiel
                            </Typography>
                            <Divider className="my-1" />
                            <Box className="mt-2">
                                <input
                                    accept=".csv"
                                    style={{ display: 'none' }}
                                    id="csv-upload-input"
                                    type="file"
                                    multiple
                                    onChange={handleCsvUpload}
                                    disabled={csvUploading}
                                />
                                <label htmlFor="csv-upload-input">
                                    <Button
                                        component="span"
                                        startDecorator={<CloudUploadIcon />}
                                        loading={csvUploading}
                                        disabled={csvUploading}
                                        className="mb-2"
                                    >
                                        Uploader des CSV
                                    </Button>
                                </label>
                                {csvError && (
                                    <Alert color="danger" className="mt-2">
                                        {csvError}
                                    </Alert>
                                )}
                                {csvSuccess && (
                                    <Alert color="success" className="mt-2">
                                        {csvSuccess}
                                    </Alert>
                                )}
                            </Box>
                        </Card>
                    </Grid>

                    <Grid xs={12} md={6} className="space-y-2">
                        <Card variant="soft">
                            <Typography level="title-md" className="mb-1">
                                Liste des publicités
                            </Typography>
                            <Divider className="my-1" />

                            <Box className="mt-2">
                                <Box className="mb-2">
                                    <Typography level="body-sm" color="neutral">
                                        Cette liste sera utilisée lors de la
                                        création/modification des publicités des
                                        parrainages.
                                    </Typography>
                                </Box>

                                {!isEditingPublicites && (
                                    <>
                                        {mandat.listePublicites && (
                                            <Stack
                                                spacing={0.5}
                                                className="mb-2 p-2"
                                            >
                                                {mandat.listePublicites
                                                    .split(';')
                                                    .map((item, index) => (
                                                        <Chip
                                                            key={index}
                                                            size="sm"
                                                            variant="soft"
                                                            color="primary"
                                                            className="p-1"
                                                        >
                                                            {item.trim()}
                                                        </Chip>
                                                    ))}
                                            </Stack>
                                        )}

                                        {!mandat.listePublicites && (
                                            <Alert
                                                color="warning"
                                                className="mb-2"
                                            >
                                                Aucune publicité définie
                                            </Alert>
                                        )}

                                        <Button
                                            startDecorator={<EditIcon />}
                                            onClick={handleStartEditPublicites}
                                            className="mt-2"
                                        >
                                            Modifier la liste
                                        </Button>
                                    </>
                                )}

                                {isEditingPublicites && (
                                    <>
                                        <Typography
                                            level="body-sm"
                                            className="mb-2"
                                        >
                                            Une option par ligne
                                        </Typography>

                                        <Textarea
                                            minRows={8}
                                            value={editedPublicites}
                                            onChange={(e) =>
                                                setEditedPublicites(
                                                    e.target.value
                                                )
                                            }
                                            placeholder="1 PARRAINAGE = annonce format A6&#10;2 PARRAINAGES = annonce format A5&#10;..."
                                            disabled={publicitesLoading}
                                            className="mb-2"
                                        />

                                        {publicitesError && (
                                            <Alert
                                                color="danger"
                                                className="mb-2"
                                            >
                                                {publicitesError}
                                            </Alert>
                                        )}

                                        {publicitesSuccess && (
                                            <Alert
                                                color="success"
                                                className="mb-2"
                                            >
                                                {publicitesSuccess}
                                            </Alert>
                                        )}

                                        <Box className="mt-2 flex gap-2">
                                            <Button
                                                onClick={handleSavePublicites}
                                                loading={publicitesLoading}
                                                disabled={publicitesLoading}
                                            >
                                                Enregistrer
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={
                                                    handleCancelEditPublicites
                                                }
                                                disabled={publicitesLoading}
                                            >
                                                Annuler
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Card>
                    </Grid>
                </Grid>

                <Box className="mt-4">
                    <Grid container spacing={2}>
                        <Grid xs={12} md={6}>
                            <Card variant="soft">
                                <Typography level="title-md" className="mb-1">
                                    Périodes
                                </Typography>
                                <Divider className="my-1" />

                                <Stack spacing={1} className="mt-2">
                                    {mandat.dateDebutAquisition && (
                                        <Box>
                                            <Typography
                                                level="body-xs"
                                                color="neutral"
                                            >
                                                Début acquisition:
                                            </Typography>
                                            <Typography>
                                                {new Date(
                                                    mandat.dateDebutAquisition
                                                ).toLocaleDateString('fr-FR')}
                                            </Typography>
                                        </Box>
                                    )}

                                    {mandat.dateFinAcquisition && (
                                        <Box>
                                            <Typography
                                                level="body-xs"
                                                color="neutral"
                                            >
                                                Fin acquisition:
                                            </Typography>
                                            <Typography>
                                                {new Date(
                                                    mandat.dateFinAcquisition
                                                ).toLocaleDateString('fr-FR')}
                                            </Typography>
                                        </Box>
                                    )}

                                    {mandat.dateArretDemande && (
                                        <Box>
                                            <Typography
                                                level="body-xs"
                                                color="neutral"
                                            >
                                                Date d'arrêt demande:
                                            </Typography>
                                            <Typography>
                                                {new Date(
                                                    mandat.dateArretDemande
                                                ).toLocaleDateString('fr-FR')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Card>
                        </Grid>

                        <Grid xs={12} md={6}>
                            <Card variant="soft">
                                <Typography level="title-md" className="mb-1">
                                    Frais de service
                                </Typography>
                                <Divider className="my-1" />

                                <Stack spacing={1} className="mt-2">
                                    <Box>
                                        <Typography
                                            level="body-xs"
                                            color="neutral"
                                        >
                                            Gestion de logo:
                                        </Typography>
                                        <Typography>
                                            {mandat.gestionDeLogo
                                                ? `${mandat.gestionDeLogo}%`
                                                : 'Non renseigné'}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography
                                            level="body-xs"
                                            color="neutral"
                                        >
                                            Frais acquisition matériel:
                                        </Typography>
                                        <Typography>
                                            {mandat.fraisDeServiceAquisitionMateriel
                                                ? `${mandat.fraisDeServiceAquisitionMateriel}%`
                                                : 'Non renseigné'}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography
                                            level="body-xs"
                                            color="neutral"
                                        >
                                            Frais ristourne:
                                        </Typography>
                                        <Typography>
                                            {mandat.fraisDeServiceRistourne
                                                ? `${mandat.fraisDeServiceRistourne}%`
                                                : 'Non renseigné'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </Card>

            <Card variant="outlined" className="mt-4">
                <Box className="mb-2 flex items-center justify-between">
                    <Typography level="h3">
                        Fichiers CSV parrainage potentiel uploadés
                    </Typography>
                </Box>
                <Divider />
                <Box className="mt-2">
                    {csvListLoading ? (
                        <Typography>Chargement des fichiers...</Typography>
                    ) : csvListError ? (
                        <Alert color="danger">{csvListError}</Alert>
                    ) : (
                        <Stack spacing={1} className="mt-2">
                            {csvFiles.length === 0 && (
                                <Typography>Aucun fichier CSV</Typography>
                            )}
                            {[...csvFiles]
                                .sort((a, b) =>
                                    a.filename.localeCompare(b.filename, 'fr', {
                                        sensitivity: 'base',
                                        numeric: true,
                                    })
                                )
                                .map((file) => (
                                    <Box
                                        key={file.id}
                                        className="flex items-center justify-between gap-2"
                                    >
                                        <div className="flex items-center gap-2">
                                            <AppIcon icon="DocumentIcon" />
                                            <div>
                                                <Typography>
                                                    {file.filename} —{' '}
                                                    {new Date(
                                                        file.createdAt
                                                    ).toLocaleString('fr-FR')}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div>
                                            {file.collaborateur ? (
                                                <Typography
                                                    level="body-sm"
                                                    color="primary"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <AppIcon icon="UserIcon" />
                                                        <strong className="ml-1">
                                                            {
                                                                file
                                                                    .collaborateur
                                                                    .userName
                                                            }
                                                        </strong>
                                                    </div>
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    level="body-sm"
                                                    color="neutral"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <AppIcon icon="UserIcon" />
                                                        <em>Non assigné</em>
                                                    </div>
                                                </Typography>
                                            )}
                                        </div>
                                        <Button
                                            size="sm"
                                            color="danger"
                                            variant="soft"
                                            startDecorator={
                                                <AppIcon icon="TrashIcon" />
                                            }
                                            onClick={() =>
                                                handleDeleteCsv(file.id)
                                            }
                                        >
                                            Supprimer
                                        </Button>
                                    </Box>
                                ))}
                        </Stack>
                    )}
                </Box>
            </Card>
        </Box>
    );
};

export default MandatDetails;
